/**
 * Issue Recorder
 *
 * Captures trace events, JS errors, console errors, and state snapshots
 * during a recording session. The collected data is attached to support
 * tickets for production debugging.
 *
 * All recording state is persisted to localStorage so it survives
 * hard navigations (window.location.assign) like logout redirects.
 *
 * Usage:
 *   import { startRecording, stopRecording, isRecording } from "../utils/issueRecorder";
 *   startRecording();
 *   // ... user reproduces the issue (even across page reloads) ...
 *   const data = stopRecording();  // → { events, errors, stateSnapshot, duration, ... }
 */

import { subscribe } from "./debugTrace";
import useAuthStore from "../stores/useAuthStore";
import useSessionStore from "../stores/useSessionStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useCartStore from "../stores/useCartStore";

const STORAGE_KEY = "__issue_recording";
const MAX_EVENTS = 500;
const MAX_ERRORS = 50;
const MAX_CONSOLE_ERRORS = 100;

let hooked = false; // whether error/console hooks are active
let unsubTrace = null;
let origOnError = null;
let origOnUnhandledRejection = null;
let origConsoleError = null;

// ── localStorage helpers ──

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage full — trim events and retry
    if (state.events.length > 50) {
      state.events = state.events.slice(-50);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
    }
  }
}

function clearState() {
  localStorage.removeItem(STORAGE_KEY);
}

// ── Public API ──

export function isRecording() {
  const state = loadState();
  return !!(state && state.active);
}

export function getStartTime() {
  const state = loadState();
  return state?.startTime || null;
}

export function startRecording() {
  if (isRecording()) return;

  const state = {
    active: true,
    startTime: Date.now(),
    events: [],
    errors: [],
    consoleErrors: [],
  };
  saveState(state);
  installHooks();
}

export function stopRecording() {
  const state = loadState();
  if (!state || !state.active) return null;

  const duration = Date.now() - state.startTime;
  const stateSnapshot = captureStateSnapshot();

  uninstallHooks();

  const data = {
    recordingVersion: 1,
    startedAt: new Date(state.startTime).toISOString(),
    duration,
    durationFormatted: formatDuration(duration),
    events: state.events,
    errors: state.errors,
    consoleErrors: state.consoleErrors,
    stateSnapshot,
    counts: {
      events: state.events.length,
      errors: state.errors.length,
      consoleErrors: state.consoleErrors.length,
    },
  };

  clearState();
  return data;
}

// ── Hooks (error + trace interception) ──

function installHooks() {
  if (hooked) return;
  hooked = true;

  const state = loadState();
  if (!state) return;

  // 1. Subscribe to trace events
  unsubTrace = subscribe((entry) => {
    const s = loadState();
    if (!s || !s.active) return;
    if (s.events.length < MAX_EVENTS) {
      s.events.push({ ...entry, _ts: Date.now() - s.startTime });
      saveState(s);
    }
  });

  // 2. Capture JS errors
  origOnError = window.onerror;
  window.onerror = (msg, source, line, col, error) => {
    const s = loadState();
    if (s && s.active && s.errors.length < MAX_ERRORS) {
      s.errors.push({
        ts: Date.now() - s.startTime,
        msg: String(msg),
        source: source ? source.split("/").pop() : "",
        line,
        col,
        stack: error?.stack?.slice(0, 500) || "",
      });
      saveState(s);
    }
    if (origOnError) return origOnError(msg, source, line, col, error);
    return false;
  };

  // 3. Capture unhandled promise rejections
  origOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const s = loadState();
    if (s && s.active && s.errors.length < MAX_ERRORS) {
      const reason = event.reason;
      s.errors.push({
        ts: Date.now() - s.startTime,
        msg: reason?.message || String(reason),
        type: "unhandledRejection",
        stack: reason?.stack?.slice(0, 500) || "",
      });
      saveState(s);
    }
    if (origOnUnhandledRejection) origOnUnhandledRejection(event);
  };

  // 4. Intercept console.error
  origConsoleError = console.error;
  console.error = (...args) => {
    const s = loadState();
    if (s && s.active && s.consoleErrors.length < MAX_CONSOLE_ERRORS) {
      s.consoleErrors.push({
        ts: Date.now() - s.startTime,
        msg: args.map((a) => (typeof a === "object" ? JSON.stringify(a)?.slice(0, 200) : String(a))).join(" ").slice(0, 500),
      });
      saveState(s);
    }
    origConsoleError.apply(console, args);
  };
}

function uninstallHooks() {
  if (!hooked) return;
  hooked = false;
  if (unsubTrace) { unsubTrace(); unsubTrace = null; }
  if (origOnError !== null) { window.onerror = origOnError; origOnError = null; }
  if (origOnUnhandledRejection !== null) { window.onunhandledrejection = origOnUnhandledRejection; origOnUnhandledRejection = null; }
  if (origConsoleError) { console.error = origConsoleError; origConsoleError = null; }
}

// ── Auto-resume on page load ──
// If a recording was active when the page reloaded (logout, navigation),
// re-install hooks so we keep capturing.

export function resumeIfActive() {
  const state = loadState();
  if (state && state.active && !hooked) {
    // Add a navigation event to mark the page reload
    if (state.events.length < MAX_EVENTS) {
      state.events.push({
        t: new Date().toISOString().slice(11, 23),
        src: "Recorder",
        msg: "Page reloaded — recording resumed",
        d: { url: window.location.pathname },
        _ts: Date.now() - state.startTime,
      });
      saveState(state);
    }
    installHooks();
  }
}

// ── Helpers ──

function formatDuration(ms) {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}m ${secs}s`;
}

function captureStateSnapshot() {
  try {
    const auth = useAuthStore.getState();
    const session = useSessionStore.getState();
    const posConfig = usePOSConfigStore.getState();
    const cart = useCartStore.getState();

    return {
      auth: {
        hasUsername: !!auth.username,
        hasTillData: !!auth.tillData,
        tillAccessIsArray: Array.isArray(auth.tillData?.tillAccess),
        hasCsBunit: !!auth.tillData?.tillAccess?.csBunit,
        tillName: auth.tillValue?.name || null,
      },
      session: {
        hasTillSession: !!session.tillSession,
        tillStatus: session.tillSession?.tillStatus || null,
        sessionId: session.tillSession?.tillSessionId?.slice(0, 8) || null,
        documentSequence: session.documentSequence,
        locked: session.locked,
      },
      posConfig: {
        posType: posConfig.posType,
        showTillOpening: posConfig.posConfig?.showTillOpening,
        showDenominations: posConfig.posConfig?.showDenominations,
        enableEstimate: posConfig.posConfig?.enableEstimate,
      },
      cart: {
        itemCount: cart.cart?.items?.length || 0,
        total: cart.cart?.total || 0,
        hasCustomer: !!cart.cart?.customer,
      },
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
    };
  } catch (err) {
    return { error: "Failed to capture state: " + err.message };
  }
}
